import {IconButton} from '@mui/material';
import {GridRenderCellParams} from '@mui/x-data-grid';
import {AurDataGridColDef} from '../types/types';
import AurIcon, {
  IconNames,
  IconColors,
} from '../components/common/aur-icon/AurIcon';
import {renderHeaderCell} from '../components/reports/intakeAudit/component/CustodianReportTableColumnDef';
import {formateNumber, getUTCDateTimeForDisplay} from './utils';
import {convertStringToNumber} from '../components/common/aur-progress-chart/utills';
import {MAXIMUM_FRACTION_DECIMAL_VOLUME} from '../components/reports/intakeAudit/component/CumulativeVolumeReportChart';
import {
  ONLY_DATE_SHOW_FORMAT,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../constants/constants';
import {t} from 'i18next';
import {AurLightTooltip} from '../components/common/aur-light-tooltip/aur-light-tooltip';

/*
Column name for client management table
*/

export const getInTakeAuditInventoryTableColumns = (): AurDataGridColDef[] => [
  {
    field: 'receivedDate',
    headerName: t('intakeAuditInventory.receivedDate'),
    width: 143,
    sortable: false,
    headerClassName: 'custom-header-inventory-table-grid',
    isDateColumn: true,
    renderCell: (params) => (
      <span className="customCellPaddingColumn1">
        {getUTCDateTimeForDisplay(params.value, ONLY_DATE_SHOW_FORMAT)}
      </span>
    ),
  },
  {
    field: 'itemLabel',
    headerName: t('intakeAuditInventory.itemLabel'),
    width: 271,
    sortable: false,
  },
  {
    field: 'receivedEvidenceUniqueName',
    headerName: t('intakeAuditInventory.receivedEvidenceUniqueName'),
    width: 171,
    sortable: false,
    renderHeader: () => (
      <div className="multiline_title header-wrap">
        <span className="typo-text">
          {t('intakeAuditInventory.receivedEvidenceUniqueName')}
        </span>
      </div>
    ),
  },

  {
    field: 'itemCategory',
    headerName: t('intakeAuditInventory.category'),
    width: 130,
    sortable: false,
  },
  {
    field: 'shippingMethod',
    headerName: t('intakeAuditInventory.shippingMethod'),
    width: 162,
    sortable: false,
  },
  {
    field: 'trackingNumber',
    headerName: t('intakeAuditInventory.trackingNumber'),
    sortable: false,
    width: 168,
  },
  {
    field: 'dispositionStatus',
    headerName: t('intakeAuditInventory.dispositionStatus'),
    sortable: false,
    width: 229,
  },
  {
    field: 'dispositionDetail',
    headerName: t('intakeAuditInventory.dispositionDetails'),
    sortable: false,
    width: 229,
    isBorderNotRequired: true,
  },
];
export interface TreeViewRowData {
  id: number;
  receivedDate: string;
  deliveryDetail: string;
  evidenceDetail: TreeViewRowData[];
  emailCompressed: number;
  externallyProcessed: number;
  otherData: number;
  children?: TreeViewRowData[];
  isExpanded?: boolean;
  toggleExpansion?: (id: number) => void;
  parentId?: number;
  isFirstChild?: boolean;
  isLastChild?: boolean;
  name?: string;
  originalData?: TreeViewRowData;
  [key: string]:
    | string
    | number
    | TreeViewRowData
    | TreeViewRowData[]
    | boolean
    | undefined
    | ((id: number) => void)
    | undefined;
}

export const getTreeDataColumn = (): AurDataGridColDef[] => [
  {
    field: 'expand',
    headerName: t('intakeAuditTreeData.receivedDate'),
    flex: 0.1467,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => {
      const isTotalRow = params.row.receivedDate === 'Totals';
      const isParent = params.row.children && params.row.children.length > 0;
      const isExpanded = params.row.isExpanded;
      return isParent || isTotalRow ? (
        <>
          {!isTotalRow && (
            <IconButton
              aria-label={
                isExpanded ? t('buttons.collapse') : t('buttons.expand')
              }
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                params.row.toggleExpansion?.(params.row.id);
              }}
              className="expand-icon"
            >
              {isExpanded ? (
                <AurIcon
                  icon={IconNames.CaretUp}
                  color={IconColors.Custom}
                  customClassName="aur-responsive-tabset-dropdown-icon-color row-expanded"
                />
              ) : (
                <AurIcon
                  icon={IconNames.CaretDown}
                  color={IconColors.Custom}
                  customClassName="aur-responsive-tabset-dropdown-icon-color"
                />
              )}
            </IconButton>
          )}
          <AurLightTooltip title={t('common.yearMonthDate')}>
            <span style={{gridRow: `span ${params.row.children.length + 1}`}}>
              {params.row.receivedDate}
            </span>
          </AurLightTooltip>
        </>
      ) : null;
    },
    renderHeader: () =>
      renderHeaderCell(t('intakeAuditTreeData.receivedDate'), {
        enableSubTitle: false,
      }),
    headerClassName: 'table-header-cell',
  },
  {
    field: 'deliveryDetails',
    headerName: t('intakeAuditTreeData.deliveryDetails'),
    flex: 0.1604,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => {
      // Check if the row is a parent and should span rows
      if (
        params.row.children &&
        params.row.children.length > 0 &&
        params.row.isExpanded
      ) {
        return (
          <div style={{gridRow: `span ${params.row.children.length + 1}`}}>
            {params.value}
          </div>
        );
      }
      // Child rows should render an empty cell for this field
      return params.row.isChild ? null : params.value;
    },
    renderHeader: () =>
      renderHeaderCell(t('intakeAuditTreeData.deliveryDetails'), {
        enableSubTitle: false,
      }),
    headerClassName: 'table-header-cell',
    cellClassName: () => 'table-data-cell',
  },
  {
    field: 'evidenceDetails',
    headerName: t('intakeAuditTreeData.evidenceDetails'),
    flex: 0.2524,
    sortable: false,
    renderHeader: () =>
      renderHeaderCell(t('intakeAuditTreeData.evidenceDetails'), {
        enableSubTitle: false,
      }),
    headerClassName: 'table-header-cell',
    cellClassName: () => 'table-data-cell',
  },
  {
    field: 'emailCompressed',
    headerName: t('intakeAuditTreeData.emailCompressedGB'),
    flex: 0.1467,
    sortable: false,
    renderHeader: () =>
      renderHeaderCell('reports.intakeAudit.custodianTable.email'),
    headerClassName: 'table-header-cell',
    cellClassName: () => 'table-data-cell',
    valueGetter: (value) => convertToDecimalPlace(value),
  },
  {
    field: 'externallyProcessed',
    //headerName: 'Externally Processed Compressed (GB)',
    flex: 0.1467,
    sortable: false,
    renderHeader: () =>
      renderHeaderCell(
        'reports.intakeAudit.custodianTable.externallyProcessed'
      ),
    headerClassName: 'table-header-cell',
    cellClassName: () => 'table-data-cell',
    valueGetter: (value) => convertToDecimalPlace(value),
  },
  {
    field: 'otherData',
    renderHeader: () =>
      renderHeaderCell('reports.intakeAudit.custodianTable.otherData', {
        enableSeperator: false,
      }),
    flex: 0.1467,
    sortable: false,
    isBorderNotRequired: true,
    headerClassName: 'table-header-cell',
    cellClassName: () => 'table-data-cell',
    valueGetter: (value) => convertToDecimalPlace(value),
  },
];

export const getSelectedTabName = (selectedTabIndex: number): string => {
  let type = '';
  switch (selectedTabIndex) {
    case 0:
      type = 'CUSTODIAN';
      break;
    case 1:
      type = 'DELIVERY';
      break;
    case 2:
      type = 'INVENTORY';
      break;
    default:
      break;
  }
  return type;
};

export const DefaultIntakeAuditData = {
  data: {
    totalCumulativeReceivedVolume: {
      email: '0',
      externallyProcessed: '0',
      otherData: '0',
    },
    custodianData: [],
    inventoryData: [],
    deliveryData: [],
  },
  hasNextPage: false,
  hasPreviousPage: false,
  page: 1,
  pageSize: TABLE_PER_PAGE_DEFAULT_NO_ROW,
  totalCount: 0,
};

export interface IReceivedDate {
  fromDate: string;
  toDate: string;
  [key: string]: string;
}

export interface IFiltersData {
  custodians?: string[];
  itemCategories?: string[];
  receivedDate: IReceivedDate;
}

export const transformDeliveryData = (dataArray: TreeViewRowData[]) => {
  let uniqueIdCounter = 1;

  return dataArray.map((data) => {
    const totalEmailCompressed = data.evidenceDetail.reduce(
      (sum: number, item: TreeViewRowData) => sum + item.emailCompressed,
      0
    );
    const totalExternallyProcessed = data.evidenceDetail.reduce(
      (sum: number, item: TreeViewRowData) => sum + item.externallyProcessed,
      0
    );
    const totalOtherData = data.evidenceDetail.reduce(
      (sum: number, item: TreeViewRowData) => sum + item.otherData,
      0
    );

    const parentId = uniqueIdCounter++;
    return {
      id: parentId,
      receivedDate: getUTCDateTimeForDisplay(
        data.receivedDate,
        ONLY_DATE_SHOW_FORMAT
      ),
      deliveryDetails: 'Subtotal:',
      evidenceDetails: '-',
      emailCompressed: totalEmailCompressed.toString(),
      externallyProcessed: totalExternallyProcessed.toString(),
      otherData: totalOtherData.toString(),
      children: data.evidenceDetail.map(
        (detail: TreeViewRowData, index: number) => ({
          id: uniqueIdCounter++,
          receivedDate: '',
          deliveryDetails:
            index === data.evidenceDetail.length - 1 ? data.deliveryDetail : '',
          evidenceDetails: detail.name,
          emailCompressed: convertToDecimalPlace(
            detail.emailCompressed.toString()
          ),
          externallyProcessed: convertToDecimalPlace(
            detail.externallyProcessed.toString()
          ),
          otherData: convertToDecimalPlace(detail.otherData.toString()),
        })
      ),
    };
  });
};

export const convertToDecimalPlace = (value: string) => {
  const numberValue = convertStringToNumber(value ?? '0');
  if (numberValue === 0) {
    return '0.000';
  }
  return numberValue < 0.001 && numberValue > 0
    ? '<0.001'
    : formateNumber(numberValue, {
        maximumFractionDigits: MAXIMUM_FRACTION_DECIMAL_VOLUME,
      });
};
